import React, { useCallback, useRef, useState } from "react";
import {
  Button,
  makeStyles,
  createStyles,
  Typography,
  Fab,
  TextField,
} from "@material-ui/core";
import { red } from "@material-ui/core/colors";
import { useHistory } from "react-router-dom";
import EmailValidator from "email-validator";

import AlertDialog from "../common/AlertDialog";
import Loading from "../common/Loading";
import TopBar from "../common/TopBar";
import ErrorDialog from "../common/ErrorDialog";
import { changeMailAddress } from "../../helper/Api";

import MailIcon from "@material-ui/icons/Mail";
import { errors, getApiError } from "../../helper/Error";

// CSSスタイルを定義
const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "calc(100vh - 58px)", // topbarの高さ分を引く(progressBar込み)
      [`${theme.breakpoints.up("xs")} and (orientation: landscape)`]: {
        minHeight: "calc(100vh - 50px)",
      },
      [theme.breakpoints.up("sm")]: {
        minHeight: "calc(100vh - 66px)",
      },
    },
    icon: {
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(5),
    },
    title: {
      width: "78%",
      maxWidth: "350px",
      marginBottom: theme.spacing(1),
    },
    text: {
      width: "78%",
      maxWidth: "350px",
      fontSize: 18,
      marginBottom: theme.spacing(3),
    },
    newMail: {
      marginBottom: theme.spacing(2),
      width: "78%",
      maxWidth: "350px",
    },
    newMailAgain: {
      marginBottom: theme.spacing(2),
      width: "78%",
      maxWidth: "350px",
    },
    flashMsg: {
      height: theme.spacing(6),
      marginBottom: theme.spacing(2),
      width: "78%",
      maxWidth: "350px",
      color: red[800],
      fontSize: 16,
    },
    button: {
      width: "78%",
      maxWidth: "350px",
      marginBottom: theme.spacing(5),
    },
  })
);

export default function PassChange() {
  const classes = useStyles();
  const history = useHistory();

  const newMail = useRef("");
  const newMailAgain = useRef("");
  const [flashMsg, setFlashMsg] = useState("");
  const [isActiveButton, setIsActiveButton] = useState(false);

  const [isOpenError, setIsOpenError] = useState(false);
  const errorRef = useRef({});
  const handleCloseError = () => {
    setIsOpenError(false);
  };

  const [isOpenResult, setIsOpenResult] = useState(false);
  const handleCloseResult = useCallback(() => {
    setIsOpenResult(false);
    history.push("/calendar");
  }, [history]);

  const validateMail = useCallback(() => {
    if (EmailValidator.validate(newMail.current) === false) {
      setFlashMsg("メールアドレスの形式が正しくありません");
      setIsActiveButton(false);
      return;
    }

    if (newMailAgain.current === "") {
      setFlashMsg("確認用のメールアドレスを入力してください");
      setIsActiveButton(false);
      return;
    }

    if (newMail.current !== newMailAgain.current) {
      setFlashMsg("入力されたメールアドレスと一致しません");
      setIsActiveButton(false);
      return;
    }

    if (newMail.current === newMailAgain.current) {
      setFlashMsg("");
      setIsActiveButton(true);
      return;
    }
  }, []);

  const handleChangeNewMail = useCallback(
    (e) => {
      newMail.current = e.target.value;

      validateMail();
    },
    [validateMail]
  );
  const handleChangeNewMailAgain = useCallback(
    (e) => {
      newMailAgain.current = e.target.value;

      validateMail();
    },
    [validateMail]
  );

  const handleClickButton = useCallback(() => {
    const fn = async () => {
      setIsChanging(true);

      await changeMailAddress(newMail.current)
        .then((res) => {
          if (res.data.error.length === 0) {
            setIsOpenResult(true);
          } else throw res.data.error;
        })
        .catch((err) => {
          errorRef.current = err.code
            ? getApiError(err)
            : errors.APP_CONNECTION_FAILED;
          setIsOpenError(true);
        });

      setIsChanging(false);
    };
    fn();
  }, []);

  const [isChanging, setIsChanging] = useState(false);

  return (
    <>
      <TopBar />

      <div className={classes.root}>
        <Fab
          className={classes.icon}
          color="primary"
          disableRipple
          disableFocusRipple
        >
          <MailIcon />
        </Fab>
        <Typography variant="h6" className={classes.title}>
          メールアドレス変更
        </Typography>

        <Typography className={classes.text} color="textSecondary">
          新しいメールアドレスを入力してください
        </Typography>

        <TextField
          label="新しいメールアドレス"
          className={classes.newMail}
          onChange={handleChangeNewMail}
        />

        <TextField
          label="新しいメールアドレス確認"
          className={classes.newMailAgain}
          onChange={handleChangeNewMailAgain}
        />

        <Typography className={classes.flashMsg}>{flashMsg}</Typography>

        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          onClick={handleClickButton}
          disabled={!isActiveButton}
        >
          変更する
        </Button>
      </div>

      <AlertDialog open={isOpenResult} onClose={handleCloseResult}>
        正常に変更されました
      </AlertDialog>

      <ErrorDialog
        open={isOpenError}
        onClose={handleCloseError}
        error={errorRef.current}
      />

      <Loading open={isChanging} />
    </>
  );
}
